import React, { useState, useEffect } from 'react'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/contexts/AuthContext'
import { Plus, Minus } from 'lucide-react'

const allowedUnits = [
  { value: 'kg', label: 'Kilograms' },
  { value: 'grams', label: 'Grams' },
  { value: 'liters', label: 'Liters' },
  { value: 'ml', label: 'Milliliters' },
  { value: 'pieces', label: 'Pieces' },
]

interface Material {
  id: string
  name: string
  unit_type: string
  current_stock: number
  min_threshold: number
  cost_per_unit: number
  supplier?: string | null
}

interface EditMaterialDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  material: Material | null
  onMaterialUpdated: () => void
  defaultTab?: 'edit' | 'restock' | 'adjust'
  selectedBranchId?: string
}

function EditMaterialDialog({ open, onOpenChange, material, onMaterialUpdated, defaultTab = 'edit', selectedBranchId }: EditMaterialDialogProps) {
  const { user } = useAuth()
  const { toast } = useToast()
  const [loading, setLoading] = useState(false)
  const [suppliers, setSuppliers] = useState<string[]>([])
  const [formData, setFormData] = useState({
    name: '',
    unit_type: allowedUnits[0].value,
    current_stock: '',
    min_threshold: '',
    cost_per_unit: '',
    supplier: ''
  })
  const [restockData, setRestockData] = useState({
    quantity: '',
    cost_per_unit: '',
    notes: '',
    real_weight: ''
  })
  const [adjustData, setAdjustData] = useState({
    new_stock: '',
    reason: ''
  })

  useEffect(() => {
    if (user) {
      loadSuppliers()
    }
  }, [user])

  useEffect(() => {
    if (material) {
      setFormData({
        name: material.name,
        unit_type: material.unit_type,
        current_stock: material.current_stock.toString(),
        min_threshold: material.min_threshold.toString(),
        cost_per_unit: material.cost_per_unit.toString(),
        supplier: material.supplier || ''
      })
      setRestockData({
        quantity: '',
        cost_per_unit: material.cost_per_unit.toString(),
        notes: '',
        real_weight: ''
      })
      setAdjustData({
        new_stock: material.current_stock.toString(),
        reason: ''
      })
    }
  }, [material])

  const loadSuppliers = async () => {
    try {
      const { data, error } = await (supabase as any)
        .from('raw_materials')
        .select('supplier')
        .eq('user_id', user?.id)
        .eq('branch_id', selectedBranchId)
        .not('supplier', 'is', null)

      if (error) {
        console.error('Error loading suppliers:', error)
        return
      }

      if (data && Array.isArray(data)) {
        const unique = Array.from(new Set(data.map((m: { supplier?: string }) => m.supplier).filter(Boolean) as string[]))
        setSuppliers(unique)
      }
    } catch (error) {
      console.error('Error loading suppliers:', error)
    }
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !material) return

    setLoading(true)
    try {
      const { error } = await (supabase as any)
        .from('raw_materials')
        .update({
          name: formData.name,
          unit_type: formData.unit_type,
          current_stock: parseFloat(formData.current_stock) || 0,
          min_threshold: parseFloat(formData.min_threshold) || 0,
          cost_per_unit: parseFloat(formData.cost_per_unit) || 0,
          supplier: formData.supplier || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', material.id)
        .eq('user_id', user.id)

      if (error) throw error

      toast({
        title: 'Material updated successfully',
        description: `Updated ${formData.name}`
      })

      onMaterialUpdated()
      onOpenChange(false)
    } catch (error) {
      console.error('Error updating material:', error)
      toast({
        title: 'Error updating material',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleRestockSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !material) return

    const quantity = parseFloat(restockData.quantity)
    const costPerUnit = parseFloat(restockData.cost_per_unit)
    const currentWeight = restockData.real_weight ? parseFloat(restockData.real_weight) : null
    let baseStock = material.current_stock

    if (currentWeight !== null && !isNaN(currentWeight)) {
      // Create a physical count record (expected = old stock, actual = currentWeight)
      const expectedStock = material.current_stock
      const wasteAmount = Math.abs(expectedStock - currentWeight)
      const wastePercentage = expectedStock > 0 ? (wasteAmount / expectedStock) * 100 : 0
      const physicalCountData = {
        material_id: material.id,
        expected_stock: expectedStock,
        actual_stock: currentWeight,
        waste_amount: wasteAmount,
        waste_percentage: wastePercentage,
        notes: `Physical count before restock. Expected: ${expectedStock} ${material.unit_type}, Actual: ${currentWeight} ${material.unit_type}. ${restockData.notes || ''}`,
        user_id: user.id,
        branch_id: selectedBranchId || null
      }
      const { error: physicalCountError } = await (supabase as any)
        .from('physical_inventory_counts')
        .insert(physicalCountData)
      if (physicalCountError) {
        console.error('Error creating physical count record:', physicalCountError)
      }
      baseStock = currentWeight // Use the measured stock as the base for restocking
    }

    if (!quantity || quantity <= 0) {
      toast({
        title: 'Invalid quantity',
        description: 'Please enter a valid quantity',
        variant: 'destructive'
      })
      return
    }
    setLoading(true)
    try {
      // Calculate new average cost
      const currentValue = baseStock * material.cost_per_unit
      const newValue = quantity * costPerUnit
      const totalQuantity = baseStock + quantity
      const newAverageCost = totalQuantity > 0 ? (currentValue + newValue) / totalQuantity : costPerUnit
      // Update material with new stock and average cost
      const { error: updateError } = await (supabase as any)
        .from('raw_materials')
        .update({
          current_stock: baseStock + quantity,
          cost_per_unit: newAverageCost,
          updated_at: new Date().toISOString()
        })
        .eq('id', material.id)
        .eq('user_id', user.id)
      if (updateError) throw updateError
      // Create inventory transaction record
      const transactionData = {
        material_id: material.id,
        transaction_type: 'restock',
        quantity: quantity,
        unit: material.unit_type,
        cost: quantity * costPerUnit,
        user_id: user.id,
        branch_id: selectedBranchId || null
      }
      await (supabase as any)
        .from('inventory_transactions')
        .insert(transactionData)
      toast({
        title: 'Material restocked successfully',
        description: `Added ${quantity} ${material.unit_type} to ${material.name}`
      })
      onMaterialUpdated()
      setRestockData({
        quantity: '',
        cost_per_unit: material.cost_per_unit.toString(),
        notes: '',
        real_weight: ''
      })
    } catch (error) {
      console.error('Error restocking material:', error)
      toast({
        title: 'Error restocking material',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  const handleAdjustSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user || !material) return

    const newStock = parseFloat(adjustData.new_stock)
    const currentStock = material.current_stock
    const adjustment = newStock - currentStock

    if (isNaN(newStock)) {
      toast({
        title: 'Invalid stock level',
        description: 'Please enter a valid number',
        variant: 'destructive'
      })
      return
    }

    setLoading(true)
    try {
      // Update material stock
      const { error: updateError } = await (supabase as any)
        .from('raw_materials')
        .update({
          current_stock: newStock,
          updated_at: new Date().toISOString()
        })
        .eq('id', material.id)
        .eq('user_id', user.id)

      if (updateError) throw updateError

      // Create inventory transaction record
      const transactionData = {
        material_id: material.id,
        transaction_type: adjustment > 0 ? 'adjustment_add' : 'adjustment_subtract',
        quantity: Math.abs(adjustment),
        unit: material.unit_type,
        cost: 0, // No cost for adjustments
        user_id: user.id,
        branch_id: selectedBranchId || null
      }
      
      console.log('Creating adjustment transaction:', transactionData)
      
      const { error: transactionError } = await (supabase as any)
        .from('inventory_transactions')
        .insert(transactionData)

      if (transactionError) {
        console.error('Error creating transaction record:', transactionError)
      } else {
        console.log('Adjustment transaction created successfully')
      }

      toast({
        title: 'Stock adjusted successfully',
        description: `Updated ${material.name} stock to ${newStock} ${material.unit_type}`
      })

      onMaterialUpdated()
      setAdjustData({
        new_stock: newStock.toString(),
        reason: ''
      })
    } catch (error) {
      console.error('Error adjusting stock:', error)
      toast({
        title: 'Error adjusting stock',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive'
      })
    } finally {
      setLoading(false)
    }
  }

  if (!material) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Material: {material.name}</DialogTitle>
          <DialogDescription>
            Update material details or restock inventory
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="edit">Edit Details</TabsTrigger>
            <TabsTrigger value="restock">Restock</TabsTrigger>
            <TabsTrigger value="adjust">Adjust Stock</TabsTrigger>
          </TabsList>

          <TabsContent value="edit" className="space-y-4">
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Beef, Lettuce, Tomatoes"
                  required
                />
              </div>

              <div>
                <Label htmlFor="unit_type">Unit</Label>
                <Select value={formData.unit_type} onValueChange={(value) => setFormData(prev => ({ ...prev, unit_type: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select unit" />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedUnits.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="current_stock">Current Stock</Label>
                <Input
                  id="current_stock"
                  type="number"
                  step="any"
                  value={formData.current_stock}
                  onChange={(e) => setFormData(prev => ({ ...prev, current_stock: e.target.value }))}
                  placeholder="0"
                  required
                />
              </div>

              <div>
                <Label htmlFor="min_threshold">Min Threshold</Label>
                <Input
                  id="min_threshold"
                  type="number"
                  step="any"
                  value={formData.min_threshold}
                  onChange={(e) => setFormData(prev => ({ ...prev, min_threshold: e.target.value }))}
                  placeholder="0"
                  required
                />
              </div>

              <div>
                <Label htmlFor="cost_per_unit">Cost per Unit</Label>
                <Input
                  id="cost_per_unit"
                  type="number"
                  step="any"
                  value={formData.cost_per_unit}
                  onChange={(e) => setFormData(prev => ({ ...prev, cost_per_unit: e.target.value }))}
                  placeholder="0.00"
                  required
                />
              </div>

              <div>
                <Label htmlFor="supplier">Supplier</Label>
                <Select value={formData.supplier} onValueChange={(value) => setFormData(prev => ({ ...prev, supplier: value }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select or enter supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map(supplier => (
                      <SelectItem key={supplier} value={supplier}>{supplier}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  className="mt-2"
                  placeholder="Or enter new supplier"
                  value={formData.supplier}
                  onChange={(e) => setFormData(prev => ({ ...prev, supplier: e.target.value }))}
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Updating...' : 'Update Material'}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent value="restock" className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Current Stock</h4>
              <p className="text-blue-700">
                {material.current_stock} {material.unit_type} at EGP {material.cost_per_unit.toFixed(2)} per {material.unit_type}
              </p>
              <p className="text-blue-600 text-sm mt-1">
                Total Value: EGP {(material.current_stock * material.cost_per_unit).toFixed(2)}
              </p>
            </div>

            <form onSubmit={handleRestockSubmit} className="space-y-4">
              <div>
                <Label htmlFor="restock_quantity">Quantity to Add</Label>
                <Input
                  id="restock_quantity"
                  type="number"
                  step="any"
                  value={restockData.quantity}
                  onChange={(e) => setRestockData(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="0"
                  required
                />
              </div>

              <div>
                <Label htmlFor="restock_current_weight">Current Weight (Optional)</Label>
                <Input
                  id="restock_current_weight"
                  type="number"
                  step="any"
                  value={restockData.real_weight}
                  onChange={(e) => setRestockData(prev => ({ ...prev, real_weight: e.target.value }))}
                  placeholder="Enter measured stock before restocking"
                />
                <p className="text-sm text-gray-500 mt-1">
                  Enter the actual measured stock before restocking (optional). This will be used to calculate waste and create a physical count record.
                </p>
              </div>

              <div>
                <Label htmlFor="restock_cost">Cost per Unit (for this batch)</Label>
                <Input
                  id="restock_cost"
                  type="number"
                  step="any"
                  value={restockData.cost_per_unit}
                  onChange={(e) => setRestockData(prev => ({ ...prev, cost_per_unit: e.target.value }))}
                  placeholder="0.00"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  This will update the average cost per unit
                </p>
              </div>

              <div>
                <Label htmlFor="restock_notes">Notes (optional)</Label>
                <Input
                  id="restock_notes"
                  value={restockData.notes}
                  onChange={(e) => setRestockData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="e.g., Fresh batch from supplier"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Restocking...' : 'Restock Material'}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>

          <TabsContent value="adjust" className="space-y-4">
            <div className="bg-orange-50 p-4 rounded-lg">
              <h4 className="font-medium text-orange-900 mb-2">Current Stock</h4>
              <p className="text-orange-700">
                {material.current_stock} {material.unit_type}
              </p>
              <p className="text-orange-600 text-sm mt-1">
                Use this to directly set the stock level (e.g., for corrections, waste, etc.)
              </p>
            </div>

            <form onSubmit={handleAdjustSubmit} className="space-y-4">
              <div>
                <Label htmlFor="adjust_stock">New Stock Level</Label>
                <Input
                  id="adjust_stock"
                  type="number"
                  step="any"
                  value={adjustData.new_stock}
                  onChange={(e) => setAdjustData(prev => ({ ...prev, new_stock: e.target.value }))}
                  placeholder="0"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">
                  This will directly set the stock level (not add to existing stock)
                </p>
              </div>

              <div>
                <Label htmlFor="adjust_reason">Reason (optional)</Label>
                <Input
                  id="adjust_reason"
                  value={adjustData.reason}
                  onChange={(e) => setAdjustData(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder="e.g., Waste, Correction, Spoilage"
                />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading}>
                  {loading ? 'Adjusting...' : 'Adjust Stock'}
                </Button>
              </DialogFooter>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

export { EditMaterialDialog } 